import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CodeBracketIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const WebDevelopment = () => {
  const technologies = [
    'React & Next.js',
    'Vue.js & Nuxt.js',
    'Node.js & Express',
    'TypeScript',
    'Tailwind CSS',
    'MongoDB & PostgreSQL',
  ];

  const features = [
    {
      icon: DevicePhoneMobileIcon,
      title: 'Responsive Design',
      description: 'Mobile-first approach ensuring perfect display on all devices.',
    },
    {
      icon: RocketLaunchIcon,
      title: 'High Performance',
      description: 'Optimized for speed with modern development practices.',
    },
    {
      icon: GlobeAltIcon,
      title: 'SEO Optimized',
      description: 'Built with search engine optimization in mind from the start.',
    },
  ];


  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-900 to-green-800 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/services"
              className="inline-flex items-center text-green-300 hover:text-green-200 mb-8 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Services
            </Link>
            
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-6">
                <CodeBracketIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold">Web Development</h1>
            </div>
            
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Custom websites and web applications built with modern technologies. From responsive design to complex web systems, we deliver solutions that perform and scale with your business.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Technologies */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-12 text-center">Technologies We Use</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-800 rounded-xl p-6 text-center border border-gray-700 hover:border-green-500 transition-all duration-300"
                  whileHover={{ y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="font-semibold text-gray-200">{tech}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-12 text-center">What We Deliver</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-green-500 transition-all duration-300"
                    whileHover={{ y: -10 }}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-6">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                    <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Services List */}
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-8">Our Web Development Services</h2>
                <div className="space-y-4">
                  {[
                    'Custom Website Development',
                    'E-commerce Solutions',
                    'Web Application Development',
                    'API Development & Integration',
                    'Database Design & Optimization',
                    'Performance Optimization',
                    'Maintenance & Support',
                    'Migration & Upgrades',
                  ].map((service, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircleIcon className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                      <span className="text-gray-300 text-lg">{service}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-3xl p-8 relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 bg-opacity-10" style={{
                    backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 2px, transparent 2px)`,
                    backgroundSize: '40px 40px'
                  }}></div>
                  
                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold mb-4 text-white">Why Choose Our Development?</h3>
                    <ul className="space-y-3 text-green-100">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        Modern Technologies & Best Practices
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        Scalable & Maintainable Code
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        Fast Delivery & Quality Assurance
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-white rounded-full mr-3"></div>
                        24/7 Support & Maintenance
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-3xl p-12 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-white/10" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '30px 30px'
              }}></div>
              
              <div className="relative z-10">
                <h3 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
                  Ready to Start Your Web Project?
                </h3>
                <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                  Let's discuss your vision and create a powerful web solution that drives results.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WebDevelopment;
